import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { GoogleMap } from '@react-google-maps/api';
import { Button } from '../components/Button';
import { Card, CardBody } from '../components/Card';
import { Alert } from '../components/Alert';
import { mapAPI } from '../api/mapAPI';
import { memberAPI } from '../api/memberAPI';
import { userAPI } from '../api/userAPI';
import { useGoogleMapsLoader } from '../hooks/useGoogleMapsLoader';

const FALLBACK_COLORS = ['#34A853', '#4285F4', '#FBBC05', '#EA4335', '#8E24AA', '#00ACC1'];
const LEADER_COLOR = '#FFD700';

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 32.82, lng: -96.95 };

// Base map style from map-maker — muted greyscale with highways, city labels,
// and arterial labels visible.  Local roads, POIs, transit, and neighborhoods hidden.
const BASE_STYLES = [
    { elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
    { elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative', elementType: 'geometry', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#555555' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#f5f5f5' }] },
    { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }, { visibility: 'on' }] },
    { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#ebebeb' }, { visibility: 'on' }] },
    { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ weight: 2 }] },
    { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'on' }, { weight: 8 }] },
    { featureType: 'road.highway', elementType: 'labels.text', stylers: [{ visibility: 'on' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
    { featureType: 'road.arterial', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.arterial', elementType: 'geometry.stroke', stylers: [{ visibility: 'on' }] },
    { featureType: 'road.arterial', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
    { featureType: 'road.local', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
    { featureType: 'water', elementType: 'geometry.fill', stylers: [{ color: '#d6d6d6' }, { visibility: 'on' }] },
    { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
];

// Label-only overlay — renders on top of polygons with opaque white halos
const LABEL_OVERLAY_STYLES = [
    { elementType: 'geometry', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.locality', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#333333' }] },
    { featureType: 'administrative.locality', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 1.5 }] },
    { featureType: 'administrative.neighborhood', stylers: [{ visibility: 'off' }] },
    { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] },
    { featureType: 'road.highway', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#333333' }] },
    { featureType: 'road.highway', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 2 }] },
    { featureType: 'road.highway', elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
    { featureType: 'road.arterial', elementType: 'labels', stylers: [{ visibility: 'on' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#555555' }] },
    { featureType: 'road.arterial', elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }, { weight: 1.5 }] },
    { featureType: 'road.arterial', elementType: 'labels.icon', stylers: [{ visibility: 'on' }] },
    { featureType: 'road.local', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off' }] },
    { featureType: 'transit', stylers: [{ visibility: 'off' }] },
    { featureType: 'water', elementType: 'labels', stylers: [{ visibility: 'off' }] },
];

const mapOptions = {
    styles: BASE_STYLES,
};

/** Parse CSS hex color to {r,g,b}. */
function hexToRgb(hex) {
    const h = hex.replace('#', '');
    return {
        r: parseInt(h.substring(0, 2), 16),
        g: parseInt(h.substring(2, 4), 16),
        b: parseInt(h.substring(4, 6), 16),
    };
}

/**
 * Canvas-based polygon overlay that renders into the mapPane (below tile layers).
 * This lets the StyledMapType label overlay sit on top of the polygon fills/strokes.
 * Adapted from map-maker's RegionPolygon.tsx.
 */
let _PolygonUnderlay = null;
function getPolygonUnderlayClass() {
    if (_PolygonUnderlay) return _PolygonUnderlay;

    class Impl extends window.google.maps.OverlayView {
        constructor(opts) {
            super();
            this._path = opts.path;
            this._insetPath = opts.insetPath;
            this._color = opts.color;
            this._fillOpacity = opts.fillOpacity;
            this._strokeOpacity = opts.strokeOpacity;
            this._strokeWeight = opts.strokeWeight;
            this._div = null;
            this._canvas = null;
        }

        onAdd() {
            this._div = document.createElement('div');
            this._div.style.position = 'absolute';
            this._div.style.pointerEvents = 'none';

            this._canvas = document.createElement('canvas');
            this._canvas.style.position = 'absolute';
            this._canvas.style.left = '0';
            this._canvas.style.top = '0';
            this._div.appendChild(this._canvas);

            // mapPane sits BELOW the tile layer → label overlay renders on top
            const panes = this.getPanes();
            if (panes) panes.mapPane.appendChild(this._div);
        }

        draw() {
            if (!this._div || !this._canvas) return;
            const projection = this.getProjection();
            if (!projection) return;

            const gm = window.google.maps;
            const fillPx = this._path.map((p) =>
                projection.fromLatLngToDivPixel(new gm.LatLng(p.lat, p.lng))
            );
            const strokePx = this._insetPath.map((p) =>
                projection.fromLatLngToDivPixel(new gm.LatLng(p.lat, p.lng))
            );

            const allPx = [...fillPx, ...strokePx];
            const pad = this._strokeWeight + 2;
            let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
            for (const pt of allPx) {
                if (pt.x < minX) minX = pt.x;
                if (pt.y < minY) minY = pt.y;
                if (pt.x > maxX) maxX = pt.x;
                if (pt.y > maxY) maxY = pt.y;
            }
            minX -= pad; minY -= pad; maxX += pad; maxY += pad;

            const w = maxX - minX;
            const h = maxY - minY;
            const dpr = window.devicePixelRatio || 1;

            this._canvas.width = w * dpr;
            this._canvas.height = h * dpr;
            this._canvas.style.width = w + 'px';
            this._canvas.style.height = h + 'px';

            this._div.style.left = minX + 'px';
            this._div.style.top = minY + 'px';
            this._div.style.width = w + 'px';
            this._div.style.height = h + 'px';

            const ctx = this._canvas.getContext('2d');
            if (!ctx) return;
            ctx.scale(dpr, dpr);
            ctx.clearRect(0, 0, w, h);

            // Draw fill on original path
            const rgb = hexToRgb(this._color);
            ctx.beginPath();
            for (let i = 0; i < fillPx.length; i++) {
                const x = fillPx[i].x - minX;
                const y = fillPx[i].y - minY;
                if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
            }
            ctx.closePath();
            ctx.fillStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${this._fillOpacity})`;
            ctx.fill();

            // Draw stroke on inset path
            if (this._strokeWeight > 0 && this._strokeOpacity > 0) {
                ctx.beginPath();
                for (let i = 0; i < strokePx.length; i++) {
                    const x = strokePx[i].x - minX;
                    const y = strokePx[i].y - minY;
                    if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
                }
                ctx.closePath();
                ctx.strokeStyle = `rgba(${rgb.r},${rgb.g},${rgb.b},${this._strokeOpacity})`;
                ctx.lineWidth = this._strokeWeight;
                ctx.stroke();
            }
        }

        onRemove() {
            if (this._div) {
                this._div.parentNode?.removeChild(this._div);
                this._div = null;
                this._canvas = null;
            }
        }
    }

    _PolygonUnderlay = Impl;
    return _PolygonUnderlay;
}

function makeSvgUrl(color, shape) {
    if (shape === 'star') {
        return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
            `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">
                <polygon points="14,2 17.5,10.5 27,10.5 19.5,16 22,25 14,20 6,25 8.5,16 1,10.5 10.5,10.5"
                    fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
            </svg>`
        );
    }
    return 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16">
            <circle cx="8" cy="8" r="7" fill="${color}" stroke="#ffffff" stroke-width="1.5"/>
        </svg>`
    );
}

export function Map() {
    const { isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const { isLoaded, loadError } = useGoogleMapsLoader();

    const [mapData, setMapData] = useState({ members: [], leaders: [], regions: [] });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mapInstance, setMapInstance] = useState(null);
    const markersRef = useRef([]);
    const polygonsRef = useRef([]);
    const infoWindowRef = useRef(null);
    const geocoderRef = useRef(null);
    const [geocodeStatus, setGeocodeStatus] = useState(null);

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchMapData();
    }, [isAuthenticated]);

    const fetchMapData = async () => {
        setLoading(true);
        try {
            const res = await mapAPI.getMapData();
            setMapData(res.data);
            setError(null);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load map data');
        } finally {
            setLoading(false);
        }
    };

    const regionColorMap = useMemo(() => {
        const map = {};
        (mapData.regions || []).forEach((r, idx) => {
            map[r.id] = r.color || FALLBACK_COLORS[idx % FALLBACK_COLORS.length];
        });
        return map;
    }, [mapData.regions]);

    const getColor = useCallback((regionId) => regionColorMap[regionId] || '#9CA3AF', [regionColorMap]);

    const formatRole = (role) => {
        return (role || '')
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    const labelOverlayRef = useRef(null);
    const onMapLoad = useCallback((map) => {
        setMapInstance(map);
        // Add label-only tile overlay on top of polygons so city names and
        // highway shields render with clean opaque white outlines.
        const labelOverlay = new window.google.maps.StyledMapType(LABEL_OVERLAY_STYLES, { name: 'Labels' });
        map.overlayMapTypes.push(labelOverlay);
        labelOverlayRef.current = labelOverlay;
    }, []);

    // Auto-geocode members/leaders that have an address but no coordinates
    useEffect(() => {
        if (!mapInstance || !window.google || loading) return;

        const needsGeocode = [];
        mapData.members.forEach((m) => {
            if (m.address && m.address.trim() && !m.latitude && !m.longitude) {
                needsGeocode.push({ ...m, _kind: 'member' });
            }
        });
        mapData.leaders.forEach((l) => {
            if (l.address && l.address.trim() && !l.latitude && !l.longitude) {
                needsGeocode.push({ ...l, _kind: 'leader' });
            }
        });

        if (needsGeocode.length === 0) return;

        // Deduplicate by address — geocode once per unique address
        const byAddress = {};
        needsGeocode.forEach((p) => {
            const key = p.address.trim().toLowerCase();
            if (!byAddress[key]) byAddress[key] = { address: p.address.trim(), people: [] };
            byAddress[key].people.push(p);
        });
        const uniqueAddresses = Object.values(byAddress);

        if (!geocoderRef.current) {
            geocoderRef.current = new window.google.maps.Geocoder();
        }
        const geocoder = geocoderRef.current;

        let cancelled = false;

        const geocodeAll = async () => {
            const updates = []; // { id, _kind, latitude, longitude }
            let completed = 0;

            for (const group of uniqueAddresses) {
                if (cancelled) return;
                setGeocodeStatus(`Geocoding ${completed + 1} of ${uniqueAddresses.length} addresses...`);

                try {
                    const result = await new Promise((resolve, reject) => {
                        geocoder.geocode({ address: group.address }, (results, status) => {
                            if (status === 'OK' && results[0]) {
                                resolve(results[0].geometry.location);
                            } else {
                                reject(new Error(status));
                            }
                        });
                    });

                    const lat = result.lat();
                    const lng = result.lng();

                    // Persist and track updates for all people at this address
                    for (const person of group.people) {
                        const payload = { latitude: lat, longitude: lng };
                        try {
                            if (person._kind === 'member') {
                                await memberAPI.updateMember(person.id, payload);
                            } else {
                                await userAPI.updateUser(person.id, payload);
                            }
                            updates.push({ id: person.id, _kind: person._kind, latitude: lat, longitude: lng });
                        } catch (e) {
                            console.warn(`Failed to save coordinates for ${person.name}:`, e);
                        }
                    }
                } catch (e) {
                    console.warn(`Geocode failed for "${group.address}":`, e.message);
                }

                completed++;
                // Small delay to avoid rate limits
                if (completed < uniqueAddresses.length) {
                    await new Promise((r) => setTimeout(r, 200));
                }
            }

            if (cancelled || updates.length === 0) {
                setGeocodeStatus(null);
                return;
            }

            // Merge geocoded coordinates into state so markers appear
            setMapData((prev) => {
                const memberUpdates = new Map();
                const leaderUpdates = new Map();
                updates.forEach((u) => {
                    if (u._kind === 'member') memberUpdates.set(u.id, u);
                    else leaderUpdates.set(u.id, u);
                });

                return {
                    ...prev,
                    members: prev.members.map((m) => {
                        const u = memberUpdates.get(m.id);
                        return u ? { ...m, latitude: u.latitude, longitude: u.longitude } : m;
                    }),
                    leaders: prev.leaders.map((l) => {
                        const u = leaderUpdates.get(l.id);
                        return u ? { ...l, latitude: u.latitude, longitude: u.longitude } : l;
                    }),
                };
            });

            setGeocodeStatus(null);
        };

        geocodeAll();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapInstance, loading]);

    // Create markers & polygons imperatively when map + data are ready
    useEffect(() => {
        if (!mapInstance || !window.google) return;
        const map = mapInstance;

        // Clean up previous markers
        markersRef.current.forEach((m) => m.setMap(null));
        markersRef.current = [];

        // Clean up previous polygons
        polygonsRef.current.forEach((p) => p.setMap(null));
        polygonsRef.current = [];

        // Close any open info window
        if (infoWindowRef.current) {
            infoWindowRef.current.close();
        }

        const gm = window.google.maps;
        const infoWindow = infoWindowRef.current || new gm.InfoWindow();
        infoWindowRef.current = infoWindow;

        const bounds = new gm.LatLngBounds();
        let hasPoints = false;

        // Group all people by lat/lng coordinate
        const groups = {};
        const makeKey = (lat, lng) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

        mapData.members.forEach((m) => {
            if (m.latitude && m.longitude) {
                const key = makeKey(m.latitude, m.longitude);
                if (!groups[key]) groups[key] = { lat: m.latitude, lng: m.longitude, people: [] };
                groups[key].people.push({ type: 'Member', regionId: m.region_id, ...m });
            }
        });

        mapData.leaders.forEach((l) => {
            if (l.latitude && l.longitude) {
                const key = makeKey(l.latitude, l.longitude);
                if (!groups[key]) groups[key] = { lat: l.latitude, lng: l.longitude, people: [] };
                groups[key].people.push({ type: (l.roles || []).map(formatRole).join(', ') || 'Leader', regionId: l.region_id, isLeader: true, ...l });
            }
        });

        // Create one marker per group
        Object.values(groups).forEach((group) => {
            const hasLeader = group.people.some((p) => p.isLeader);
            const firstPerson = group.people[0];
            const color = hasLeader ? LEADER_COLOR : getColor(firstPerson.regionId);
            const shape = hasLeader ? 'star' : 'circle';
            const size = hasLeader ? 28 : 16;

            const position = { lat: group.lat, lng: group.lng };
            const marker = new gm.Marker({
                position,
                map,
                icon: {
                    url: makeSvgUrl(color, shape),
                    scaledSize: new gm.Size(size, size),
                    anchor: new gm.Point(size / 2, size / 2),
                },
                zIndex: hasLeader ? 10 : 1,
            });

            marker.addListener('click', () => {
                const getAge = (p) => {
                    if (!p.birthdate) return null;
                    const bd = new Date(p.birthdate);
                    if (isNaN(bd.getTime())) return null;
                    const today = new Date();
                    let age = today.getFullYear() - bd.getFullYear();
                    const m = today.getMonth() - bd.getMonth();
                    if (m < 0 || (m === 0 && today.getDate() < bd.getDate())) age--;
                    return age;
                };
                const sorted = [...group.people].sort((a, b) => {
                    const ageA = getAge(a) ?? -1;
                    const ageB = getAge(b) ?? -1;
                    return ageB - ageA;
                });
                const lines = sorted.map((p) => {
                    let html = `<div style="margin-bottom:6px">`;
                    const displayName = p.name || '-';
                    const age = getAge(p);
                    html += `<strong>${displayName}</strong>`;
                    if (age !== null) html += ` <span style="font-size:12px;color:#6b7280">(${age})</span>`;
                    if (p.isLeader) html += ` <span style="font-size:12px;color:#6b7280">(${p.type})</span>`;
                    if (p.email) html += `<br/><a href="mailto:${p.email}" style="font-size:13px;color:#6366f1;text-decoration:none">${p.email}</a>`;
                    if (p.phone) html += `<br/><a href="tel:${p.phone}" style="font-size:13px;color:#6366f1;text-decoration:none">${p.phone}</a>`;
                    html += `</div>`;
                    return html;
                });
                const address = group.people.find((p) => p.address)?.address;
                const content = `<div style="font-family:sans-serif;max-width:280px">
                    ${group.people.length > 1 ? `<p style="margin:0 0 6px;font-size:12px;color:#9CA3AF">${group.people.length} people at this location</p>` : ''}
                    ${lines.join('')}
                    ${address ? `<p style="margin:6px 0 0;font-size:12px"><a href="https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}" target="_blank" rel="noopener noreferrer" style="color:#6366f1;text-decoration:none">${address}</a></p>` : ''}
                </div>`;
                infoWindow.setContent(content);
                infoWindow.open(map, marker);
            });

            markersRef.current.push(marker);
            bounds.extend(position);
            hasPoints = true;
        });

        // Region boundary polygons — canvas underlays in mapPane (below tile layer)
        // so the StyledMapType label overlay renders on top.
        const Underlay = getPolygonUnderlayClass();
        const insetFactor = 0.015;

        mapData.regions.forEach((r) => {
            if (!r.boundary) return;
            const coords = typeof r.boundary === 'string' ? JSON.parse(r.boundary) : r.boundary;
            if (!Array.isArray(coords) || coords.length < 3) return;
            const color = getColor(r.id);

            const centroid = coords.reduce(
                (acc, c) => ({ lat: acc.lat + c.lat / coords.length, lng: acc.lng + c.lng / coords.length }),
                { lat: 0, lng: 0 }
            );
            const insetCoords = coords.map((c) => ({
                lat: c.lat + (centroid.lat - c.lat) * insetFactor,
                lng: c.lng + (centroid.lng - c.lng) * insetFactor,
            }));

            // Canvas overlay rendered in mapPane (below tiles & label overlay)
            const underlay = new Underlay({
                path: coords,
                insetPath: insetCoords,
                color,
                fillOpacity: 0.25,
                strokeOpacity: 0.6,
                strokeWeight: 2,
            });
            underlay.setMap(map);
            polygonsRef.current.push(underlay);

            // Region name label
            const labelMarker = new gm.Marker({
                position: centroid,
                map,
                icon: { path: gm.SymbolPath.CIRCLE, scale: 0 },
                label: { text: r.name, color, fontWeight: 'bold', fontSize: '15px', className: 'region-label' },
                clickable: false,
            });
            polygonsRef.current.push(labelMarker);

            coords.forEach((c) => {
                bounds.extend({ lat: c.lat, lng: c.lng });
                hasPoints = true;
            });
        });

        if (hasPoints) {
            map.fitBounds(bounds);
        }

        // Close info window on map click
        const clickListener = map.addListener('click', () => {
            infoWindow.close();
        });

        return () => {
            gm.event.removeListener(clickListener);
        };
    }, [mapData, getColor, mapInstance]);

    if (!isAuthenticated) {
        return (
            <div className="flex items-center justify-center min-h-[60vh] p-4">
                <Card>
                    <CardBody>
                        <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-2">Not authenticated</h2>
                        <p className="text-sm text-slate-500 mb-4">Please login to view the map.</p>
                        <Button onClick={() => navigate('/login')}>Login</Button>
                    </CardBody>
                </Card>
            </div>
        );
    }

    const mapsAvailable = isLoaded && !loadError;

    return (
        <div className="flex flex-col h-[calc(100vh-3.5rem)] lg:h-screen">
            {error && <div className="px-4 pt-2"><Alert type="error" onClose={() => setError(null)}>{error}</Alert></div>}

            {loading || !isLoaded ? (
                <p className="text-center text-slate-500 py-8">{loading ? 'Loading map data...' : 'Loading Google Maps...'}</p>
            ) : loadError ? (
                <div className="text-center text-slate-500 py-8">
                    <p>Failed to load Google Maps: {loadError.message}</p>
                </div>
            ) : !mapsAvailable ? (
                <div className="text-center text-slate-500 py-8">
                    <p>Google Maps API key not configured.</p>
                    <p className="text-sm mt-1">Set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file.</p>
                </div>
            ) : (
                <>
                    <div className="flex-1 lg:rounded-lg lg:mx-4 lg:mt-4 lg:mb-2 overflow-hidden">
                        <GoogleMap
                            mapContainerStyle={mapContainerStyle}
                            center={defaultCenter}
                            zoom={10}
                            options={mapOptions}
                            onLoad={onMapLoad}
                        />
                    </div>

                    {geocodeStatus && (
                        <p className="text-center text-xs text-slate-500 py-1">{geocodeStatus}</p>
                    )}

                    <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-4 py-2 text-xs text-slate-500 dark:text-slate-400 border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                        <div className="flex items-center gap-3">
                            <span className="font-semibold text-slate-600 dark:text-slate-300">Markers:</span>
                            <span className="flex items-center gap-1.5">
                                <span className="inline-block w-3 h-3 rounded-full" style={{ background: '#9CA3AF' }}></span> Members
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span style={{ color: LEADER_COLOR, fontSize: '14px' }}>&#9733;</span> Leaders / Assistants
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span className="font-semibold text-slate-600 dark:text-slate-300">Regions:</span>
                            {mapData.regions.map((r) => (
                                <span key={r.id} className="flex items-center gap-1.5">
                                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: getColor(r.id) }}></span>
                                    {r.name}
                                </span>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

export default Map;
